import { type RefObject, useEffect, useState } from 'react';

const PARALLAX_RANGE_VH = 1;
const MAX_TRANSLATE_PERCENT = 8;
const ELEMENT_MAX_TRANSLATE_PX = 48;

/** Stronger travel so IRM mountains clearly lag the foreground on scroll. */
export const MOUNTAINS_PARALLAX_INTENSITY = 120;

/**
 * Scroll-based vertical parallax for full-viewport hero media.
 * Returns a percentage translate suitable for `transform: translateY(...)`.
 */
export const useParallax = () => {
  const [translateY, setTranslateY] = useState(0);
  const [enabled, setEnabled] = useState(true);

  useEffect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    const update = () => setEnabled(!mq.matches);
    update();
    mq.addEventListener('change', update);
    return () => mq.removeEventListener('change', update);
  }, []);

  useEffect(() => {
    if (!enabled) {
      setTranslateY(0);
      return;
    }

    let frame = 0;

    const update = () => {
      const progress = Math.min(
        window.scrollY / (window.innerHeight * PARALLAX_RANGE_VH),
        1,
      );
      setTranslateY(-progress * MAX_TRANSLATE_PERCENT);
    };

    const onScroll = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(update);
    };

    update();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll, { passive: true });

    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
    };
  }, [enabled]);

  return translateY;
};

/**
 * Element-relative parallax: moves slower than scroll while the target is in view.
 * Returns a pixel translate for decorative backgrounds (e.g. IRM mountains).
 *
 * For mountains, progress maps so the graphic starts lower (peaks in view) and
 * drifts upward more slowly than the section, revealing the mountain downward.
 *
 * @param ref - Element to track relative to the viewport
 * @param intensity - Max travel in px at the extremes of the element's visibility
 * @returns translateY in pixels
 */
export const useElementParallax = (
  ref: RefObject<HTMLElement | null>,
  intensity = ELEMENT_MAX_TRANSLATE_PX,
) => {
  const [translateY, setTranslateY] = useState(0);
  const [enabled, setEnabled] = useState(true);

  useEffect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    const update = () => setEnabled(!mq.matches);
    update();
    mq.addEventListener('change', update);
    return () => mq.removeEventListener('change', update);
  }, []);

  useEffect(() => {
    if (!enabled) {
      setTranslateY(0);
      return;
    }

    let frame = 0;

    const update = () => {
      const el = ref.current;
      if (!el) {
        return;
      }

      const rect = el.getBoundingClientRect();
      const viewH = window.innerHeight;
      // 0 when section top hits bottom of viewport; 1 when section bottom leaves top
      const travel = viewH + rect.height;
      if (travel <= 0) {
        return;
      }
      const progress = (viewH - rect.top) / travel;
      const clamped = Math.min(Math.max(progress, 0), 1);
      // Start shifted down (peaks), ease upward as section scrolls — slower than content.
      setTranslateY((1 - clamped) * intensity - intensity * 0.25);
    };

    const onScroll = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(update);
    };

    update();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll, { passive: true });

    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
    };
  }, [enabled, intensity, ref]);

  return translateY;
};
